import { useState, useRef, useEffect } from 'react';
import { Dialog, CONFIG, api, useToast } from './_barrel.js';


        // Two-step registration for a REAL device (not a self-trial):
        //   1. Record consent  — upload the subject's signed authority (stored as
        //      evidence) and choose scope.  -> consent_id
        //   2. Add the device  — paste the MAID + platform.               -> device_id
        // On success the Exposure tab reloads and selects the new device, ready
        // for DSAR requests.
        function AeaRegisterDeviceDialog({ subjectId, subjectName, token, onClose, onDone }) {
            const toast = useToast();
            const fileRef = useRef(null);
            const [step, setStep] = useState(1);
            const [busy, setBusy] = useState(false);
            const [err, setErr] = useState(null);

            // Step 1 — consent
            const [scope, setScope] = useState('dsar_and_erasure');
            const [expiryDays, setExpiryDays] = useState('180');
            const [consentFile, setConsentFile] = useState(null);
            const [consentId, setConsentId] = useState(null);

            // Authority already captured at onboarding? Reuse it, skip the upload.
            const [checking, setChecking] = useState(true);
            const [fromOnboarding, setFromOnboarding] = useState(false);
            const [consentExpiry, setConsentExpiry] = useState(null);

            // Step 2 — device
            const [maid, setMaid] = useState('');
            const [platform, setPlatform] = useState('android');
            const [label, setLabel] = useState('');

            useEffect(() => {
                let live = true;
                (async () => {
                    try {
                        const r = await fetch(
                            `${CONFIG.apiBase}/api/v1/aea/subjects/${subjectId}/active-consent`,
                            { headers: { Authorization: `Bearer ${token}` } });
                        if (live && r.ok) {
                            const b = await r.json();
                            setConsentId(b.consent_record_id);
                            setConsentExpiry(b.expires_at);
                            setFromOnboarding(true);
                            setStep(2);   // consent already on file — go straight to the device
                        }
                    } catch (e) { /* no consent on file — fall through to manual capture */ }
                    finally { if (live) setChecking(false); }
                })();
                return () => { live = false; };
            }, [subjectId, token]);

            async function submitConsent() {
                if (!consentFile) { setErr('Attach the signed consent document first.'); return; }
                setBusy(true); setErr(null);
                try {
                    const fd = new FormData();
                    fd.append('principal_id', subjectId);
                    fd.append('scope', scope);
                    fd.append('article6_basis', 'consent');
                    const days = parseInt(expiryDays, 10);
                    if (days > 0) {
                        const d = new Date(); d.setDate(d.getDate() + days);
                        fd.append('expires_at', d.toISOString());
                    }
                    fd.append('file', consentFile);
                    const resp = await fetch(`${CONFIG.apiBase}/api/v1/aea/consent/upload`,
                        { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
                    if (!resp.ok) {
                        let msg = `Could not record consent (${resp.status})`;
                        try { const j = await resp.json(); msg = j.detail || j.error?.message || msg; } catch (_) {}
                        throw new Error(msg);
                    }
                    const data = await resp.json();
                    setConsentId(data.id);
                    setStep(2);
                } catch (ex) { setErr(ex.message); }
                finally { setBusy(false); }
            }

            async function submitDevice() {
                const raw = maid.trim();
                if (!raw) { setErr('Enter the advertising ID (MAID).'); return; }
                setBusy(true); setErr(null);
                try {
                    const res = await api('/api/v1/aea/devices', {
                        method: 'POST',
                        body: JSON.stringify({
                            consent_record_id: consentId, maid: raw,
                            platform, label: label.trim() || null,
                        }),
                    }, token);
                    toast('Device registered — now log a DSAR request');
                    if (onDone) onDone(res.id);
                } catch (ex) {
                    setErr((ex.data && ex.data.detail) || ex.message);
                } finally { setBusy(false); }
            }

            const inputStyle = { width: '100%', background: 'var(--zone-2)', color: 'var(--text)', border: '1px solid var(--veil)', padding: '10px 12px', fontSize: '14px' };

            return (
                <Dialog onClose={onClose} title={`Register device — ${subjectName || 'subject'}`}>
                    <div className="flex gap-2 mb-5">
                        {[1, 2].map(n => (
                            <div key={n} className="flex-1 h-1" style={{ background: step >= n ? 'var(--gold)' : 'var(--line)' }} />
                        ))}
                    </div>

                    {checking && (
                        <div className="text-sm text-muted py-6 text-center">Checking for authority on file…</div>
                    )}

                    {!checking && step === 1 && (
                        <div className="space-y-4">
                            <div className="text-sm text-muted leading-relaxed">
                                <b style={{ color: 'var(--text)' }}>Step 1 — Record consent.</b> Upload the subject's
                                signed authority for you to request their data from brokers on their behalf. It is
                                stored as tamper-evident evidence and underpins every DSAR you send.
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-muted">Scope of authority</label>
                                <select value={scope} onChange={e => setScope(e.target.value)} style={{ ...inputStyle, marginTop: 6 }}>
                                    <option value="dsar_and_erasure">Access + erasure (recommended)</option>
                                    <option value="dsar_only">Access only</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-muted">Consent valid for</label>
                                <select value={expiryDays} onChange={e => setExpiryDays(e.target.value)} style={{ ...inputStyle, marginTop: 6 }}>
                                    <option value="90">90 days</option>
                                    <option value="180">180 days</option>
                                    <option value="365">1 year</option>
                                </select>
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-muted">Signed consent document *</label>
                                <input ref={fileRef} type="file"
                                       accept=".pdf,.jpg,.jpeg,.png,.heic,.doc,.docx,.txt"
                                       onChange={e => setConsentFile(e.target.files && e.target.files[0])}
                                       style={{ display: 'none' }} />
                                <div className="flex items-center gap-3 mt-2">
                                    <button type="button" className="btn-ghost" onClick={() => fileRef.current && fileRef.current.click()}>
                                        {consentFile ? 'Change file' : 'Choose file'}
                                    </button>
                                    <span className="text-xs text-muted">{consentFile ? consentFile.name : 'PDF, scan or photo of the signed form'}</span>
                                </div>
                            </div>
                            {err && <div className="text-sm" style={{ color: 'var(--red)' }}>{err}</div>}
                            <div className="flex justify-end gap-3 pt-2">
                                <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                                <button type="button" className="btn-gold" disabled={busy || !consentFile} onClick={submitConsent}>
                                    {busy ? 'Recording…' : 'Record consent →'}
                                </button>
                            </div>
                        </div>
                    )}

                    {!checking && step === 2 && (
                        <div className="space-y-4">
                            {fromOnboarding && (
                                <div className="px-4 py-3 text-sm" style={{ background: 'var(--green-bg)', border: '1px solid var(--green)', borderRadius: 'var(--radius-sm)', color: 'var(--text-soft)' }}>
                                    <b style={{ color: 'var(--green)' }}>✓ Authority on file.</b> The subject granted
                                    data-broker authority at onboarding{consentExpiry ? ` (valid to ${new Date(consentExpiry).toLocaleDateString()})` : ''} —
                                    no upload needed. Just add the device below.
                                </div>
                            )}
                            <div className="text-sm text-muted leading-relaxed">
                                <b style={{ color: 'var(--text)' }}>{fromOnboarding ? 'Add the device.' : 'Step 2 — Add the device.'}</b> Paste the subject's
                                advertising ID. It is hashed immediately — the raw ID is never stored, returned or logged.
                            </div>
                            <div className="px-3 py-2 text-xs text-muted" style={{ background: 'var(--zone-1)', border: '1px solid var(--line)', borderRadius: 'var(--radius-sm)' }}>
                                Find it on the phone — <b>Android:</b> Settings → Google → Ads → “Your advertising ID”.
                                <b> iPhone:</b> the IDFA (via a MAID-reader app; Apple hides it in Settings). Reset or
                                missing? Brokers may still hold historical data — continue anyway.
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-muted">Advertising ID (MAID) *</label>
                                <input value={maid} onChange={e => setMaid(e.target.value)} autoFocus
                                       placeholder="e.g. 38400000-8cf0-11bd-b23e-10b96e40000d"
                                       style={{ ...inputStyle, marginTop: 6, fontFamily: 'var(--font-mono, monospace)' }} />
                            </div>
                            <div className="flex gap-3">
                                <div style={{ flex: 1 }}>
                                    <label className="font-mono text-xs uppercase tracking-widest text-muted">Platform</label>
                                    <select value={platform} onChange={e => setPlatform(e.target.value)} style={{ ...inputStyle, marginTop: 6 }}>
                                        <option value="android">Android (GAID)</option>
                                        <option value="ios">iOS (IDFA)</option>
                                    </select>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label className="font-mono text-xs uppercase tracking-widest text-muted">Label (optional)</label>
                                    <input value={label} onChange={e => setLabel(e.target.value)}
                                           placeholder="e.g. Personal iPhone" style={{ ...inputStyle, marginTop: 6 }} />
                                </div>
                            </div>
                            {err && <div className="text-sm" style={{ color: 'var(--red)' }}>{err}</div>}
                            <div className="flex justify-between items-center pt-2">
                                {fromOnboarding
                                    ? <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
                                    : <button type="button" className="btn-ghost" onClick={() => { setErr(null); setStep(1); }}>← Back</button>}
                                <button type="button" className="btn-gold" disabled={busy || !maid.trim()} onClick={submitDevice}>
                                    {busy ? 'Registering…' : 'Register device'}
                                </button>
                            </div>
                        </div>
                    )}
                </Dialog>
            );
        }

export { AeaRegisterDeviceDialog };
